"use client"

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Heart, Shield, Award, GraduationCap, Stethoscope, Users, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { useScrollY } from '@/hooks/use-scroll-progress'
const CLINIC = '/images/Clinic Photos'

const philosophy = [
  {
    icon: Heart,
    title: 'Compassionate Care',
    description: 'A gentle approach that helps children feel safe and parents feel heard.',
    color: 'from-peach to-rose-400',
  },
  {
    icon: Shield,
    title: 'Evidence-Based Medicine',
    description: 'Treatment guided by current pediatric research and globally accepted guidelines.',
    color: 'from-teal to-teal-dark',
  },
  {
    icon: Users,
    title: 'Unhurried Consultations',
    description: 'Clear explanations so every parent leaves feeling confident and informed.',
    color: 'from-amber-400 to-orange-500',
  },
]

const experience = [
  {
    role: 'Assistant Professor (Pediatrics)',
    place: 'Dr. S.N. Medical College, Umaid & MDM Hospitals, Jodhpur',
    period: 'May 2025 – Present',
  },
  {
    role: 'Pediatrician & Lactation Consultant',
    place: "Dr. Arushi Singhal Joshi's Pediatric Clinic, Jodhpur",
    period: 'May 2025 – Present',
    note: 'Comprehensive pediatric care, newborn support, breastfeeding guidance, and preventive child health.',
  },
  {
    role: 'Senior Resident (Pediatrics)',
    place: 'Topiwala National Medical College & BYL Nair Hospital, Mumbai',
    period: 'Feb 2024 – Feb 2025',
  },
  {
    role: 'Pediatrician',
    place: 'Breach Candy Hospital, Mumbai',
    period: 'Sep 2023 – Feb 2024',
  },
  {
    role: 'Resident Doctor (MD Pediatrics)',
    place: 'Dr. S.N. Medical College & Associated Hospitals, Jodhpur',
    period: 'Jul 2020 – Jun 2023',
    note: 'Extensive training in NICU, PICU, emergency pediatrics, and neonatal care.',
  },
  {
    role: 'Internship (MBBS)',
    place: 'Topiwala National Medical College & BYL Nair Hospital, Mumbai',
    period: 'Feb 2018 – Feb 2019',
  },
]

function getYears() {
  const now = new Date()
  const start = new Date(2018, 1, 1)
  const y = now.getFullYear() - start.getFullYear()
  const m = now.getMonth() - start.getMonth()
  return Math.max(0, m >= 0 ? y : y - 1)
}

export function AboutSection({ clinicPhotos }: { clinicPhotos: string[] }) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const scrollY = useScrollY()
  const { ref: introRef, isIntersecting: introVisible } = useIntersectionObserver({ threshold: 0.06, once: true })
  const { ref: credRef, isIntersecting: credVisible } = useIntersectionObserver({ threshold: 0.05, once: true })
  const years = getYears()
  const parallaxOffset = scrollY * 0.06
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const photos = clinicPhotos.length ? clinicPhotos : ['1.jpeg']
  const heroPhoto = photos[0]
  const smallPhotos = photos.slice(1)

  const lightboxSrc = lightboxIndex === null ? null : `${CLINIC}/${photos[lightboxIndex]}`

  const gotoPrev = () => {
    if (lightboxIndex === null) return
    setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length)
  }

  const gotoNext = () => {
    if (lightboxIndex === null) return
    setLightboxIndex((lightboxIndex + 1) % photos.length)
  }

  useEffect(() => {
    if (lightboxIndex === null) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null)
      if (e.key === 'ArrowLeft') gotoPrev()
      if (e.key === 'ArrowRight') gotoNext()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex])

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-28"
    >
      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Close image"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="relative w-full max-w-4xl max-h-[85vh] aspect-[4/3] rounded-2xl overflow-hidden bg-slate-900 shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={gotoPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={gotoNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/40 text-white/90 text-xs border border-white/10">
              {(lightboxIndex ?? 0) + 1} / {photos.length}
            </div>

            <Image
              src={lightboxSrc}
              alt="Clinic photo"
              fill
              className="object-contain"
              sizes="(max-width: 768px) 95vw, 900px"
              priority
            />
          </div>
        </div>
      )}

      {/* Ambient background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-teal/[0.04] rounded-full blur-3xl pointer-events-none"
        style={{ transform: `translateY(${parallaxOffset}px)` }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-peach/[0.05] rounded-full blur-3xl pointer-events-none"
        style={{ transform: `translateY(-${parallaxOffset}px)` }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-20 lg:space-y-24">

        {/* ── 1. INTRO: headline left, image right ── */}
        <div ref={introRef} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Copy */}
          <div className={`space-y-6 ${introVisible ? 'reveal visible' : 'reveal'}`}>
            <div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6 hover:scale-105 transition-transform duration-300 ${introVisible ? 'scale-in' : ''}`}>
                About
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-[1.15]">
                Your Child&apos;s Health,{' '}
                <span className="text-gradient">Our Priority</span>
              </h2>
            </div>
            <p className="text-lg leading-[29px] font-normal" style={{ color: 'lab(35.5623 -1.74978 -15.4316)' }}>
              Dr. Arushi Singhal Joshi is a pediatrician and IBCLC-certified lactation consultant based in Jodhpur.
              She cares for babies, children, and teenagers, covering newborn checks, breastfeeding support,
              vaccinations, growth tracking, and acute illness.
            </p>
            <p className="text-lg leading-[29px] font-normal" style={{ color: 'lab(35.5623 -1.74978 -15.4316)' }}>
              Her consultations are unhurried. She explains things in plain language, involves the child when
              it helps, and recommends medicines or tests only when needed. Every family leaves feeling clear
              and confident about their child&apos;s health.
            </p>
          </div>

          {/* Image stack */}
          <div className={`relative ${introVisible ? 'reveal visible' : 'reveal'}`} style={{ transitionDelay: '100ms' }}>
            {/* One big + rest small (auto-fit bento) */}
            <div className="grid grid-cols-6 gap-2 auto-rows-[64px] sm:auto-rows-[72px] max-w-[460px] mx-auto lg:ml-auto lg:mr-0">
              {/* Big image */}
              <button
                type="button"
                className="relative col-span-6 sm:col-span-4 row-span-4 rounded-3xl overflow-hidden shadow-xl cursor-zoom-in focus:outline-hidden focus:ring-2 focus:ring-teal/50"
                onClick={() => setLightboxIndex(0)}
                aria-label="Open clinic photo"
              >
                <Image
                  src={`${CLINIC}/${heroPhoto}`}
                  alt="Clinic interior — Dr. Arushi's practice, Jodhpur"
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 92vw, 460px"
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/20 via-transparent to-transparent pointer-events-none" />
                {/* Overlay caption */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg inline-flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal flex items-center justify-center shrink-0">
                      <Award className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-navy leading-tight">IBCLC</p>
                      <p className="text-[11px] text-muted-foreground leading-tight">Internationally Certified</p>
                    </div>
                  </div>
                </div>
              </button>

              {/* Small images */}
              {smallPhotos.map((f) => (
                <button
                  key={f}
                  type="button"
                  className="relative col-span-2 row-span-2 rounded-xl overflow-hidden bg-slate-100 cursor-zoom-in focus:outline-hidden focus:ring-2 focus:ring-teal/50"
                  onClick={() => setLightboxIndex(photos.indexOf(f))}
                  aria-label="Open clinic photo"
                >
                  <Image
                    src={`${CLINIC}/${f}`}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 30vw, 150px"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        

        {/* ── QUALIFICATIONS + PHILOSOPHY (left) / EXPERIENCE (right) ── */}
        <div ref={credRef} className="grid sm:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-start">

          {/* Left column: Qualifications + Philosophy */}
          <div className="space-y-6">
            {/* Professional Qualifications */}
            <div className={`space-y-4 ${credVisible ? 'reveal visible' : 'reveal'}`}>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-teal shrink-0" />
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-navy">Professional Qualifications</h3>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200/80 shadow-sm divide-y divide-slate-100">
                <div className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-teal mb-3">Medical Education</p>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-navy text-sm">MD (Pediatrics)</p>
                      <p className="text-xs text-muted-foreground">Dr. Sampurnanand Medical College, Jodhpur</p>
                      <span className="inline-block mt-1 text-[11px] font-medium text-teal bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap">Jun 2020 – Jun 2023</span>
                    </div>
                    <div>
                      <p className="font-semibold text-navy text-sm">MBBS</p>
                      <p className="text-xs text-muted-foreground">Topiwala National Medical College, Mumbai</p>
                      <span className="inline-block mt-1 text-[11px] font-medium text-teal bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap">Aug 2013 – Feb 2018</span>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-teal mb-3">Certifications</p>
                  <p className="font-semibold text-navy text-sm">IBCLC</p>
                  <p className="text-xs text-muted-foreground">International Board of Lactation Consultant Examiners (IBLCE)</p>
                  <span className="inline-block mt-1 text-[11px] font-medium text-teal bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap">2025</span>
                </div>
              </div>
            </div>

            {/* Trust & Care Philosophy */}
            <div className={`space-y-4 ${credVisible ? 'reveal visible' : 'reveal'}`} style={{ transitionDelay: '60ms' }}>
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-teal shrink-0" />
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-navy">Trust & Care Philosophy</h3>
              </div>
              <div className="space-y-3">
                {philosophy.map((item, i) => {
                  const Icon = item.icon
                  return (
                    <div
                      key={item.title}
                      className={`group flex items-start gap-4 p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${credVisible ? 'reveal visible' : 'reveal'}`}
                      style={{ transitionDelay: `${80 + i * 60}ms` }}
                    >
                      <div className="relative w-9 h-9 rounded-xl bg-teal/10 flex items-center justify-center shrink-0 overflow-hidden group-hover:bg-teal/15 transition-colors">
                        <Icon className="w-4 h-4 text-teal relative z-10" />
                        <div className={`absolute inset-0 bg-linear-to-br ${item.color} opacity-0 group-hover:opacity-15 transition-opacity duration-300`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy text-sm mb-0.5 group-hover:text-teal transition-colors">{item.title}</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Right column: Experience */}
          <div className={`space-y-4 ${credVisible ? 'reveal visible' : 'reveal'}`} style={{ transitionDelay: '80ms' }}>
            <div className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-teal shrink-0" />
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-navy">Experience</h3>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200/80 shadow-sm divide-y divide-slate-100">
              {experience.map((job) => (
                <div key={`${job.role}-${job.period}`} className="p-5">
                  <p className="font-semibold text-navy text-sm">{job.role}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{job.place}</p>
                  <span className="inline-block mt-1 text-[11px] font-medium text-teal bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap">{job.period}</span>
                  {job.note && <p className="text-xs text-muted-foreground/80 mt-1.5 italic">{job.note}</p>}
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    </section>
  )
}
